import React from 'react';
import { Link } from 'react-router-dom';
import {
    Server,
    Users,
    Zap,
    Shield,
    Target,
    Plane,
    Rocket,
    Anchor,
    ArrowRight,
    CheckCircle2,
    Building2,
    Globe,
    Award,
    TrendingUp,
    Bot
} from 'lucide-react';
import PageLayout from '../Layout/PageLayout';
import PageHeader from '../PageHeader/PageHeader';
import ModernCTA from '../CTA/ModernCTA';
import './ModernWhatWeDo.scss';

const ModernWhatWeDo = () => {
    const technologySolutions = [
        {
            icon: <Bot size={32} />,
            title: 'AI Automation & Workflows',
            description: 'Internal and external automations, in-app AI copilots, and AI social media content workflows in Make and n8n, deployed on AWS.',
            path: '/ai-automation',
            features: ['AI Copilots', 'Make & n8n Workflows', 'Social Media Content'],
            featured: true
        },
        {
            icon: <Server size={32} />,
            title: 'IT Modernization',
            description: 'Transform legacy systems with cloud migration, infrastructure refresh, and modern network solutions.',
            path: '/it-modernization',
            features: ['Cloud Migration', 'Infrastructure Refresh', 'Network Modernization']
        },
        {
            icon: <Users size={32} />,
            title: 'Workforce Experience',
            description: 'Enhance productivity with AV systems, digital signage, collaboration tools, and helpdesk support.',
            path: '/workforce',
            features: ['AV Systems', 'Collaboration Tools', 'Digital Signage']
        },
        {
            icon: <Zap size={32} />,
            title: 'Digital Transformation',
            description: 'Accelerate innovation with process automation, data analytics, and AI integration.',
            path: '/digital',
            features: ['Process Automation', 'Data Analytics', 'AI Integration']
        },
        {
            icon: <Shield size={32} />,
            title: 'Cybersecurity',
            description: 'Protect your assets with comprehensive security solutions, threat monitoring, and compliance.',
            path: '/cybersecurity',
            features: ['Threat Detection', 'Compliance', 'Security Operations']
        }
    ];

    const defenseSolutions = [
        {
            icon: <Target size={32} />,
            title: 'Army Solutions',
            description: 'Field-tested infrastructure for mission-critical operations including tactical communications and ruggedized computing.',
            path: '/army',
            badge: 'Ground Forces'
        },
        {
            icon: <Anchor size={32} />,
            title: 'Navy Solutions',
            description: 'Maritime technology solutions for shipboard communications, combat systems, and fleet operations.',
            path: '/navy',
            badge: 'Maritime'
        },
        {
            icon: <Plane size={32} />,
            title: 'Air Force Solutions',
            description: 'Advanced technology for air dominance including defense systems, communications, and base operations.',
            path: '/air-force',
            badge: 'Aviation'
        },
        {
            icon: <Rocket size={32} />,
            title: 'Space Force Solutions',
            description: 'Cutting-edge technology for space operations including satellite systems and domain awareness.',
            path: '/space-force',
            badge: 'Space'
        },
        {
            icon: <Shield size={32} />,
            title: 'Marine Corps Solutions',
            description: 'Expeditionary technology for rapid deployment, combat systems, and tactical operations.',
            path: '/marine-corps',
            badge: 'Expeditionary'
        }
    ];

    const stats = [
        { icon: <Building2 size={28} />, value: '500+', label: 'Projects Delivered' },
        { icon: <Globe size={28} />, value: '50+', label: 'States Served' },
        { icon: <Award size={28} />, value: '20+', label: 'Years Experience' },
        { icon: <TrendingUp size={28} />, value: '99%', label: 'Client Satisfaction' }
    ];

    return (
        <PageLayout>
            <PageHeader
                title="Core Competencies"
                subtitle="Comprehensive technology solutions for government and enterprise"
                breadcrumbs={[
                    { label: 'Home', path: '/' },
                    { label: 'Core Competencies' }
                ]}
            />

            {/* Hero Section */}
            <section className="jk-whatwedo-hero">
                <div className="jk-whatwedo-hero__container">
                    <div className="jk-whatwedo-hero__content">
                        <span className="jk-section-label">
                            <Zap size={16} />
                            Technology Excellence
                        </span>
                        <h2 className="jk-whatwedo-hero__title">
                            Delivering <span className="highlight">Mission-Critical</span> Solutions
                        </h2>
                        <p className="jk-whatwedo-hero__description">
                            From IT modernization to defense technology, Jaak Tech provides comprehensive
                            solutions that enable organizations to achieve their mission objectives with reliability,
                            security, and innovation.
                        </p>
                        <div className="jk-whatwedo-hero__stats">
                            {stats.map((stat, index) => (
                                <div key={index} className="jk-whatwedo-hero__stat">
                                    <div className="jk-whatwedo-hero__stat-icon">{stat.icon}</div>
                                    <div className="jk-whatwedo-hero__stat-value">{stat.value}</div>
                                    <div className="jk-whatwedo-hero__stat-label">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Technology Solutions Section */}
            <section className="jk-whatwedo-solutions">
                <div className="jk-whatwedo-solutions__container">
                    <div className="jk-whatwedo-solutions__header">
                        <span className="jk-section-label">
                            <Server size={16} />
                            Technology Solutions
                        </span>
                        <h2 className="jk-whatwedo-solutions__title">
                            Enterprise
                            <span className="highlight"> Technology</span>
                        </h2>
                        <p className="jk-whatwedo-solutions__subtitle">
                            Modern solutions to transform your organization's technology infrastructure
                        </p>
                    </div>

                    <div className="jk-whatwedo-solutions__grid">
                        {technologySolutions.map((solution, index) => (
                            <Link to={solution.path} key={index} className={`jk-whatwedo-solutions__card${solution.featured ? ' jk-whatwedo-solutions__card--featured' : ''}`}>
                                <div className="jk-whatwedo-solutions__card-icon">
                                    {solution.icon}
                                </div>
                                <h3 className="jk-whatwedo-solutions__card-title">{solution.title}</h3>
                                <p className="jk-whatwedo-solutions__card-description">{solution.description}</p>
                                <ul className="jk-whatwedo-solutions__card-features">
                                    {solution.features.map((feature, idx) => (
                                        <li key={idx}>
                                            <CheckCircle2 size={14} />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="jk-whatwedo-solutions__card-link">
                                    Learn More <ArrowRight size={16} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Defense Solutions Section */}
            <section className="jk-whatwedo-defense">
                <div className="jk-whatwedo-defense__container">
                    <div className="jk-whatwedo-defense__header">
                        <span className="jk-section-label">
                            <Shield size={16} />
                            Defense Solutions
                        </span>
                        <h2 className="jk-whatwedo-defense__title">
                            Military Branch
                            <span className="highlight"> Technology</span>
                        </h2>
                        <p className="jk-whatwedo-defense__subtitle">
                            Specialized solutions for each branch of the U.S. Armed Forces
                        </p>
                    </div>

                    <div className="jk-whatwedo-defense__grid">
                        {defenseSolutions.map((solution, index) => (
                            <Link to={solution.path} key={index} className="jk-whatwedo-defense__card">
                                <div className="jk-whatwedo-defense__card-header">
                                    <div className="jk-whatwedo-defense__card-icon">
                                        {solution.icon}
                                    </div>
                                    <span className="jk-whatwedo-defense__card-badge">{solution.badge}</span>
                                </div>
                                <h3 className="jk-whatwedo-defense__card-title">{solution.title}</h3>
                                <p className="jk-whatwedo-defense__card-description">{solution.description}</p>
                                <div className="jk-whatwedo-defense__card-link">
                                    Explore Solutions <ArrowRight size={16} />
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <ModernCTA />
        </PageLayout>
    );
};

export default ModernWhatWeDo;
